import { useNavigate } from 'react-router-dom'
import { FileQuestion, BookOpen, Layers, Calendar, TrendingUp } from 'lucide-react'
import { useDashboardOverview } from '../../../hooks/usePYQAnalytics'
import { usePYQAnalyticsStore } from '../../../stores/pyqAnalyticsStore'
import ErrorSection from '../shared/ErrorSection'

interface KPICardProps {
  title: string
  value: string | number
  subtitle?: string
  icon: React.ReactNode
  color: string
  bgColor: string
  onClick?: () => void
}

function KPICard({ title, value, subtitle, icon, color, bgColor, onClick }: KPICardProps) {
  const content = (
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm text-gray-600">{title}</p>
        <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
        {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
      </div>
      <div
        className="p-2 rounded-lg"
        style={{ backgroundColor: bgColor }}
      >
        <div style={{ color }}>{icon}</div>
      </div>
    </div>
  )

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow text-left w-full cursor-pointer"
      >
        {content}
      </button>
    )
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow">
      {content}
    </div>
  )
}

export default function KPICardsSection() {
  const navigate = useNavigate()
  const { yearRangeStart, yearRangeEnd } = usePYQAnalyticsStore()
  const { data, isLoading, isError, refetch } = useDashboardOverview({
    yearRangeStart,
    yearRangeEnd,
  })

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="bg-white rounded-lg border border-gray-200 p-4 h-24 animate-pulse">
            <div className="h-4 w-24 bg-gray-200 rounded mb-2" />
            <div className="h-8 w-16 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    )
  }

  if (isError) {
    return <ErrorSection title="Failed to load KPIs" onRetry={() => refetch()} height="h-24" />
  }

  if (!data) return null

  const yearRange = data.yearRange || { start: yearRangeStart, end: yearRangeEnd }
  const trendsSummary = data.trendsSummary || { increasingTopics: 0, decreasingTopics: 0 }

  const kpis: KPICardProps[] = [
    {
      title: 'Total Questions',
      value: (data.totalQuestions || 0).toLocaleString(),
      subtitle: `${yearRange.start} - ${yearRange.end}`,
      icon: <FileQuestion className="w-5 h-5" />,
      color: '#3B82F6',
      bgColor: '#EFF6FF',
    },
    {
      title: 'Topics Covered',
      value: data.totalTopics || 0,
      icon: <BookOpen className="w-5 h-5" />,
      color: '#10B981',
      bgColor: '#ECFDF5',
    },
    {
      title: 'Units',
      value: data.totalUnits || 0,
      icon: <Layers className="w-5 h-5" />,
      color: '#F59E0B',
      bgColor: '#FFFBEB',
    },
    {
      title: 'Exams Analyzed',
      value: data.totalExams || 0,
      icon: <Calendar className="w-5 h-5" />,
      color: '#8B5CF6',
      bgColor: '#F5F3FF',
    },
    {
      title: 'Trending Topics',
      value: trendsSummary.increasingTopics || 0,
      subtitle: `${trendsSummary.decreasingTopics || 0} declining`,
      icon: <TrendingUp className="w-5 h-5" />,
      color: '#EF4444',
      bgColor: '#FEF2F2',
      onClick: () => navigate('/analytics/past-exams/trending'),
    },
  ]

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
      {kpis.map((kpi, index) => (
        <KPICard key={index} {...kpi} />
      ))}
    </div>
  )
}
